import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send cookies on all requests
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // null = checking session, false = unauthenticated, object = authenticated
  const [auth, setAuth] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setAuth({ user: data.user, company: data.company });
      return data;
    } catch (e) {
      setAuth(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setAuth({ user: data.user, company: data.company });
    return data;
  }, []);

  const signup = useCallback(async ({ company_name, email, password, name }) => {
    const { data } = await axios.post(
      `${API}/auth/signup`,
      { company_name, email, password, name },
      { withCredentials: true }
    );
    setAuth({ user: data.user, company: data.company });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // ignore
    }
    setAuth(false);
  }, []);

  const value = {
    auth,
    user: auth && auth.user,
    company: auth && auth.company,
    isLoading: auth === null,
    isAuthenticated: !!auth,
    login,
    signup,
    logout,
    refresh,
    formatApiErrorDetail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
